import { useState, ChangeEvent, FormEvent } from 'react';

// TypeScript interfaces for form fields and validation
interface FieldValues {
  [key: string]: any;
}

interface Errors {
  [key: string]: string;
}

interface UseFormOptions<T extends FieldValues> {
  initialValues: T;
  validate?: (values: T) => Errors;
}

/**
 * useForm Hook - A reusable and dynamic form handler
 * @param initialValues - Initial values for the form fields
 * @param validate - Optional validation function that returns an object of errors
 */
export default function useForms<T extends FieldValues>({
  initialValues,
  validate,
}: UseFormOptions<T>) {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Errors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle input changes
  const handleChange = (event: { target: { name; value } }) => {
    const { name, value } = event.target;
    setValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>,
    onSubmit: (values: T) => Promise<void> | void
  ) => {
    event.preventDefault();

    if (validate) {
      const validationErrors = validate(values);
      setErrors(validationErrors);
      if (Object.keys(validationErrors).length > 0) {
        setIsSubmitting(false); // Pastikan state dikembalikan
        return;
      }
    }

    setIsSubmitting(true);

    try {
      await onSubmit(values);
    } catch (error) {
      console.error('Error during form submission:', error);
    } finally {
      setIsSubmitting(false); // Reset state setelah submit
    }
  };

  return {
    values,
    errors,
    isSubmitting,
    handleChange,
    handleSubmit,
    setValues,
  };
}
